import React, { useCallback, useEffect, useState } from "react";
import { AgGridReact } from "ag-grid-react";

import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { downloadSettingExcel } from "src/utils/blob/blobUtil";
import { formatLocalDate } from "src/utils/date/dateUtils";
import { showError, showSuccess, showWarning } from "src/utils/toast/toastUtil";

import {
	deleteTQC1050_R01,
	downloadExcel_TQC1050_R01,
	downloadReportPdf_TQC1050_R01,
	getTQC1050_R01AllCount,
	getTQC1050_R01List,
	saveTQC1050_R01,
	TQC1050_R01Params,
} from "./Api_TQC1050_R01";
import {
	defaultTQC1050_R01Dto,
	TQC1050_R01Dto,
} from "./Dto_TQC1050_R01";
import { TQC1050_R01Filter, useTQC1050_R01Store } from "./Store_TQC1050_R01";

interface Props {
	gridRef: React.RefObject<AgGridReact<TQC1050_R01Dto> | null>;
}

export const Content_TQC1050_R01 = ({ gridRef }: Props) => {
	const { setLoadingSpinner } = useLoadingSpinnerStore();

	const {
		TQC1050_R01Filter,
		TQC1050_R01Info,
		setTQC1050_R01PastFilter,
		TQC1050_R01List,
		setTQC1050_R01List,
		setOriginalTQC1050_R01List,
		selectedTQC1050_R01List,
		setSelectedTQC1050_R01List,
		setAllCount,
		setScrollTop,
		initData: initStoreData,
	} = useTQC1050_R01Store();

	const [clickable, setClickable] = useState(true);

	const defaultTQC1050_R01Row = useCallback(() => ({ ...defaultTQC1050_R01Dto }), []);

	/**
	 * 화면 필터를 서버 조회 파라미터로 변환한다.
	 * 빈 값은 undefined 로 만들어 쿼리스트링에서 제외한다.
	 */
	const getParams = (filter: TQC1050_R01Filter): TQC1050_R01Params => ({
		compCd: filter.compCd || undefined,
		transportDt: filter.transportDt ? formatLocalDate(filter.transportDt) : undefined,
		customCd: filter.customCd || undefined,
		brandCd: filter.brandCd || undefined,
		inoutClass: filter.inoutClass || undefined,
	});

	/** 서버 응답을 그리드 행으로 정규화한다. */
	const toGridRows = useCallback(
		(list: TQC1050_R01Dto[]): TQC1050_R01Dto[] =>
			list.map((item, idx) => ({
				...defaultTQC1050_R01Dto,
				...item,
				valueIndex: idx + 1,
				inserted: false,
				isModified: false,
			})),
		[],
	);

	// ── 조회 ─────────────────────────────────────────────────
	const fetchData = useCallback(async () => {
		try {
			setLoadingSpinner(true);
			setScrollTop(null);

			const params = getParams(TQC1050_R01Filter);
			const response = await getTQC1050_R01List("search", params);
			const data = toGridRows(response.data.data ?? []);

			setTQC1050_R01List(data);
			setOriginalTQC1050_R01List(data);
			setSelectedTQC1050_R01List([]);

			const countResponse = await getTQC1050_R01AllCount(params);
			setAllCount(countResponse.data.data ?? data.length);
			setTQC1050_R01PastFilter(TQC1050_R01Filter);

			data.length > 0
				? showSuccess(`조회 완료 (${data.length}건)`)
				: showWarning("조회된 데이터가 없습니다.");
		} catch {
			showError("조회 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [
		TQC1050_R01Filter, toGridRows,
		setLoadingSpinner, setScrollTop, setTQC1050_R01List,
		setOriginalTQC1050_R01List, setSelectedTQC1050_R01List,
		setAllCount, setTQC1050_R01PastFilter,
	]);

	/** 저장·삭제 후 현재 조건으로 다시 읽어 목록/원본을 최신화한다. */
	const refetch = useCallback(async () => {
		const params = getParams(TQC1050_R01Filter);
		const response = await getTQC1050_R01List("search", params);
		const data = toGridRows(response.data.data ?? []);
		setTQC1050_R01List(data);
		setOriginalTQC1050_R01List(data);
		setSelectedTQC1050_R01List([]);
	}, [
		TQC1050_R01Filter, toGridRows,
		setTQC1050_R01List, setOriginalTQC1050_R01List, setSelectedTQC1050_R01List,
	]);

	// ── 저장 (수정된 행만) ───────────────────────────────────
	/**
	 * isModified 인 행만 추려 저장한다.
	 * inspectValue1_1~1_4 값을 상세 테이블 컬럼과 동일한 필드명으로 전송한다.
	 */
	const handleSaveClick = useCallback(async () => {
		const modifiedRows = TQC1050_R01List.filter((row) => row.isModified);

		if (modifiedRows.length === 0) {
			showWarning("변경된 데이터가 없습니다.");
			return;
		}

		const saveRows = modifiedRows.map((row) => ({
			...row,
			inspectType: "IQC",
			inspectorEmp: TQC1050_R01Info.inspectorEmp,
			inspectorEmpNm: TQC1050_R01Info.inspectorEmpNm,
			approvorEmpno: TQC1050_R01Info.approvorEmpno,
			approvorEmpNm: TQC1050_R01Info.approvorEmpNm,
		}));

		const noInspector = saveRows.filter((row) => !row.inspectorEmp);
		if (noInspector.length > 0) {
			showWarning("검사자를 입력해주세요.");
			return;
		}

		try {
			setLoadingSpinner(true);
			await saveTQC1050_R01(saveRows);
			await refetch();
			showSuccess("저장이 완료되었습니다.");
		} catch {
			showError("저장 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [TQC1050_R01Info, TQC1050_R01List, refetch, setLoadingSpinner]);

	// ── 삭제 (검사결과만) ────────────────────────────────────
	/** 선택한 행의 검사결과를 삭제한다. 입고내역은 남는다. */
	const handleDeleteClick = useCallback(async () => {
		if (selectedTQC1050_R01List.length === 0) {
			showWarning("삭제할 행을 선택해주세요.");
			return;
		}

		const selected = new Set(selectedTQC1050_R01List);
		const targets = TQC1050_R01List.filter(
			(row) => selected.has(row.valueIndex.toString()) && row.inspectNo,
		);
		if (targets.length === 0) {
			showWarning("삭제할 검사결과가 없습니다.");
			return;
		}

		try {
			setLoadingSpinner(true);
			await deleteTQC1050_R01(targets);
			await refetch();
			showSuccess("삭제가 완료되었습니다.");
		} catch {
			showError("삭제 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [selectedTQC1050_R01List, TQC1050_R01List, refetch, setLoadingSpinner]);

	// ── 엑셀 ─────────────────────────────────────────────────
	const handleExcelClick = useCallback(async () => {
		try {
			setLoadingSpinner(true);
			const response = await downloadExcel_TQC1050_R01(getParams(TQC1050_R01Filter));
			if (!response.data.data) {
				showWarning("엑셀로 내보낼 데이터가 없습니다.");
				return;
			}
			downloadSettingExcel({
				responseData: response.data.data,
				fileName: "수입검사결과등록.xlsx",
			});
		} catch {
			showError("엑셀 다운로드 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [TQC1050_R01Filter, setLoadingSpinner]);

	// ── 출력 (소재검사서 PDF) ────────────────────────────────
	/**
	 * 출력 버튼 처리. 현재 조회조건으로 소재검사서 PDF(Blob)를 받아 새 탭으로 연다.
	 * 열람 후 5초 뒤 Object URL 을 해제해 메모리 누수를 막는다. (TQC1040_R01 패턴)
	 */
	const handlePrintClick = useCallback(async () => {
		try {
			setLoadingSpinner(true);
			const response = await downloadReportPdf_TQC1050_R01(getParams(TQC1050_R01Filter));
			const blob = new Blob([response.data], { type: "application/pdf" });
			const fileUrl = URL.createObjectURL(blob);
			window.open(fileUrl, "_blank");
			setTimeout(() => URL.revokeObjectURL(fileUrl), 5000);
		} catch (error) {
			// 응답을 blob 으로 받으므로 서버 오류 본문도 blob 으로 온다.
			// 텍스트로 풀어 원인을 그대로 보여준다. (서버가 text/plain 으로 내려줌)
			const data = (error as { response?: { data?: unknown } })?.response?.data;
			const detail = data instanceof Blob ? (await data.text()).trim() : "";
			showError(detail || "출력 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [TQC1050_R01Filter, setLoadingSpinner]);

	// ── 초기화 ───────────────────────────────────────────────
	const initContainerData = useCallback(() => {
		initStoreData();
		gridRef.current?.api?.deselectAll();
		gridRef.current?.api?.setGridOption("rowData", []);
	}, [gridRef, initStoreData]);

	return {
		clickable, setClickable,
		defaultTQC1050_R01Row,
		fetchData,
		handleSaveClick,
		handleDeleteClick,
		handleExcelClick,
		handlePrintClick,
		initContainerData,
	};
};
